import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Package } from 'lucide-react';
import { Skeleton } from '../components/ui/Skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import { TenantHeading } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';
import { usePermission } from '../hooks/usePermission';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { PageHeader } from '../components/ui/PageHeader';
import { LastSynced } from '../components/ui/LastSynced';

interface ECRImage {
  tags: string[];
  digest: string;
  size: number;
  pushedAt: Date | string;
  age?: number;
}

interface ECRRepository {
  repositoryName: string;
  repositoryUri: string;
  customer: string;
  imageType: string;
  createdAt: Date | string;
  images: ECRImage[];
}

interface ECRCustomerImages {
  [customer: string]: ECRRepository[];
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

function formatDate(date: Date | string): string {
  if (!date) return 'N/A';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DevImages() {
  const { accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('dev_images', 'read');
  const tenantMetaMap = useTenantMetaMap(canView);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerImages, setCustomerImages] = useState<ECRCustomerImages>({});
  const [syncedAt, setSyncedAt] = useState<string | null>(null);

  const tenantsToQuery = selectedTenants.length > 0 ? selectedTenants : accessibleTenants;

  useActivityHeartbeat('cache:dev_images', canView);

  const fetchImages = useCallback(async () => {
    if (!canView) return;
    if (tenantsToQuery.length === 0) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch all tenants to get their display names and colors
      const tenantsResponse = await apiClient.getTenants();
      const tenantsData = Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || [];
      const tenantMap: Map<string, { name?: string; color?: string | null }> = new Map(
        tenantsData.map((t: any) => [String(t.code || '').toLowerCase(), { name: t.name, color: t.color }]),
      );

      const response = await apiClient.getDevImages();
      const images = response.images || {};
      setSyncedAt(response.syncedAt ?? null);

      // Map customer codes to display names and filter by selected tenants
      const mappedImages: any = {};
      Object.entries(images).forEach(([customer, repos]) => {
        const customerLower = customer.toLowerCase();

        // Filter by selected tenants
        if (tenantsToQuery.length > 0 && !tenantsToQuery.some(t => t.toLowerCase() === customerLower)) {
          return;
        }

        const tenantInfo = tenantMap.get(customerLower);
        const displayName = tenantInfo?.name || customer;

        // Sort repositories by name
        const sortedRepos = (repos as ECRRepository[]).sort((a, b) =>
          a.repositoryName.localeCompare(b.repositoryName),
        );

        mappedImages[displayName] = {
          repos: sortedRepos,
          color: tenantInfo?.color,
          tenantCode: customerLower,
        };
      });

      // Sort by tenant name
      const sortedImages: any = {};
      Object.keys(mappedImages)
        .sort((a, b) => a.localeCompare(b))
        .forEach(key => {
          sortedImages[key] = mappedImages[key];
        });

      setCustomerImages(sortedImages);
    } catch (err: any) {
      console.error('Failed to fetch dev images:', err);
      setError(err.message || 'Failed to load dev images');
    } finally {
      setIsLoading(false);
    }
  }, [canView, tenantsToQuery.join(',')]);

  useEffect(() => {
    void fetchImages();
  }, [fetchImages]);

  // Refresh only when backend cache version changes.
  useCacheEvents({
    enabled: canView,
    cacheNames: ['dev_images'],
    onEvent: () => {
      void fetchImages();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton variant="circle" className="w-5 h-5" />
                <Skeleton className="h-6 w-40" />
              </div>
              <Skeleton className="h-4 w-24" />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((j) => (
                <Card key={j} className="w-full min-w-0 overflow-hidden">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <Skeleton variant="circle" className="w-10 h-10" />
                        <div className="flex-1 min-w-0">
                          <Skeleton className="h-5 w-40" />
                          <Skeleton className="h-4 w-24 mt-2" />
                        </div>
                      </div>
                      <Skeleton className="h-5 w-16" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {[1, 2, 3].map((k) => (
                        <Skeleton key={k} className="h-12 w-full" />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="py-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-600 dark:text-red-400 mb-4" />
            <p className="text-red-600 dark:text-red-400">Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalRepos = Object.values(customerImages).reduce((sum, data: any) => sum + data.repos.length, 0);
  const totalImages = Object.values(customerImages).reduce(
    (sum, data: any) => sum + data.repos.reduce((repoSum: number, repo: ECRRepository) => repoSum + repo.images.length, 0),
    0
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <PageHeader
          icon={Package}
          iconColor="text-indigo-600 dark:text-indigo-400"
          iconBgColor="bg-indigo-100 dark:bg-indigo-900/30"
          description="Docker container images in AWS ECR"
          count={totalImages}
          countLabel={`${totalRepos} ${totalRepos !== 1 ? 'repositories' : 'repository'}`}
          countColor="text-indigo-600 dark:text-indigo-400"
        />
        <LastSynced iso={syncedAt} />
      </div>

      {Object.keys(customerImages).length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No ECR images found.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(customerImages).map(([customer, data]: [string, any]) => (
            <div key={customer} className="space-y-4">
              <div className="flex items-center justify-between">
                <TenantHeading
                  tenant={resolveTenantMeta(tenantMetaMap, data.tenantCode ?? customer, {
                    name: customer,
                    color: data.color,
                  })}
                  title={customer}
                />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {data.repos.length} repository{data.repos.length !== 1 ? 'ies' : ''}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {data.repos.map((repo: ECRRepository) => (
                  <Card key={repo.repositoryName} className="hover:shadow-lg transition-shadow w-full min-w-0 overflow-hidden">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                            <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg truncate">{repo.repositoryName}</CardTitle>
                            <CardDescription className="mt-1 capitalize">
                              {repo.imageType} image
                            </CardDescription>
                          </div>
                        </div>
                        <Badge variant="outline" className="ml-2">
                          {repo.images.length} image{repo.images.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {repo.images.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                            No images in this repository
                          </p>
                        ) : (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {repo.images.slice(0, 20).map((img, idx) => (
                              <div
                                key={`${img.digest}-${idx}`}
                                className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-mono text-xs text-gray-900 dark:text-gray-100 truncate">
                                      {img.tags.join(', ') || '<untagged>'}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                      <span>{formatBytes(img.size)}</span>
                                      <span>•</span>
                                      <span>{formatDate(img.pushedAt)}</span>
                                      {img.age !== undefined && (
                                        <>
                                          <span>•</span>
                                          <span>{img.age}d old</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {repo.images.length > 20 && (
                              <div className="text-center text-xs text-gray-500 dark:text-gray-400 py-2">
                                +{repo.images.length - 20} more images
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
